import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, DateDecoder, EnumDecoder, field, IntegerDecoder, MapDecoder, NumberDecoder, StringDecoder } from '@common/encoding';
import { v4 as uuidv4 } from 'uuid';
import { NamedObject } from './Event.js';
import { baseInMemoryFilterCompilers, compileToInMemoryFilter, createInMemoryFilterCompiler } from './filters/InMemoryFilter.js';
import { documentInMemoryFilterCompilers } from './filters/inMemoryFilterDefinitions.js';
import { RecordAnswerDecoder, RecordAnswerMapDecoder } from './members/records/RecordAnswer.js';
import { RecordCategory } from './members/records/RecordCategory.js';
import { upgradePriceFrom2To4DecimalPlaces } from './upgradePriceFrom2To4DecimalPlaces.js';
export var DocumentStatus;
(function (DocumentStatus) {
    DocumentStatus["Draft"] = "Draft";
    DocumentStatus["MissingData"] = "MissingData";
    DocumentStatus["Published"] = "Published";
    DocumentStatus["Deleted"] = "Deleted";
})(DocumentStatus || (DocumentStatus = {}));
export class DocumentStatusHelper {
    static getName(status) {
        switch (status) {
            case DocumentStatus.Draft: return $t(`%Kn`);
            case DocumentStatus.MissingData: return $t(`%Ni`);
            case DocumentStatus.Published: return $t(`%m5`);
            case DocumentStatus.Deleted: return $t(`%1FX`);
        }
    }
    static getColor(status) {
        switch (status) {
            case DocumentStatus.Draft: return 'info';
            case DocumentStatus.MissingData: return 'tertiary';
            case DocumentStatus.Published: return 'secundary';
            case DocumentStatus.Deleted: return 'error';
        }
    }
}
export class DocumentSettings extends AutoEncoder {
    name = '';
    maxAge = null;
    minPrice = null;
    minPricePaid = null;
    /**
     * Fields defined by the template that can be set.
     */
    fieldAnswers = new Map();
    /**
     * Defines where to automatically find the answer for a given question
     */
    linkedFields = new Map();
    maxAgeSevereDisability = null;
}
__decorate([
    field({ decoder: StringDecoder, version: 178 })
], DocumentSettings.prototype, "name", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true })
], DocumentSettings.prototype, "maxAge", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, nullable: true })
], DocumentSettings.prototype, "minPrice", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, version: 348 }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, nullable: true })
], DocumentSettings.prototype, "minPricePaid", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(RecordAnswerDecoder) }),
    field({
        decoder: RecordAnswerMapDecoder,
        version: 252,
        upgrade: (old) => {
            const map = new Map();
            for (const answer of old) {
                map.set(answer.settings.id, answer);
            }
            return map;
        },
    })
], DocumentSettings.prototype, "fieldAnswers", void 0);
__decorate([
    field({ decoder: new MapDecoder(StringDecoder, new ArrayDecoder(StringDecoder)) })
], DocumentSettings.prototype, "linkedFields", void 0);
__decorate([
    field({
        decoder: IntegerDecoder,
        nullable: true,
        version: 415,
    })
], DocumentSettings.prototype, "maxAgeSevereDisability", void 0);
export class DocumentTemplateDefinition extends AutoEncoder {
    type = null;
    name = '';
    fieldCategories = [];
    /**
     * Questions filled out for each selected group / cycle combination
     */
    groupFieldCategories = [];
    /**
     * Questions that are different for each document. They can be linked to specific fields of members later on for automatic linking.
     */
    documentFieldCategories = [];
    defaultMaxAge = null;
    defaultMaxAgeSevereDisability = null;
    defaultMinPrice = null;
    defaultMinPricePaid = null;
    allowChangingMaxAge = false;
    allowChangingMinPrice = false;
    allowChangingMinPricePaid = false;
    exportFieldCategories = [];
    xmlExport = null;
    xmlExportDescription = null;
    transformForDiff() {
        // Exclude from all diffs
        return null;
    }
}
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 194, upgrade: () => 'fiscal' })
], DocumentTemplateDefinition.prototype, "type", void 0);
__decorate([
    field({ decoder: StringDecoder })
], DocumentTemplateDefinition.prototype, "name", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(RecordCategory) })
], DocumentTemplateDefinition.prototype, "fieldCategories", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(RecordCategory) })
], DocumentTemplateDefinition.prototype, "groupFieldCategories", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(RecordCategory) })
], DocumentTemplateDefinition.prototype, "documentFieldCategories", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true })
], DocumentTemplateDefinition.prototype, "defaultMaxAge", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true })
], DocumentTemplateDefinition.prototype, "defaultMaxAgeSevereDisability", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, nullable: true })
], DocumentTemplateDefinition.prototype, "defaultMinPrice", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, version: 348 }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, nullable: true })
], DocumentTemplateDefinition.prototype, "defaultMinPricePaid", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 347 })
], DocumentTemplateDefinition.prototype, "allowChangingMaxAge", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 347 })
], DocumentTemplateDefinition.prototype, "allowChangingMinPrice", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 348 })
], DocumentTemplateDefinition.prototype, "allowChangingMinPricePaid", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(RecordCategory), version: 179 })
], DocumentTemplateDefinition.prototype, "exportFieldCategories", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 179 })
], DocumentTemplateDefinition.prototype, "xmlExport", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 179 })
], DocumentTemplateDefinition.prototype, "xmlExportDescription", void 0);
export class DocumentTemplateGroup extends AutoEncoder {
    id;
    group;
    /**
     * @deprecated
     */
    cycle = 0;
    /**
     * Answers for groupFieldCategories for this group
     */
    fieldAnswers = new Map();
    getDiffName() {
        return this.group.name;
    }
    isRecordEnabled(_record) {
        return true;
    }
    doesMatchFilter(filter) {
        return true;
    }
    getRecordAnswers() {
        return this.fieldAnswers;
    }
    patchRecordAnswers(patch) {
        return this.patch({
            fieldAnswers: patch,
        });
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], DocumentTemplateGroup.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder, field: 'groupId' }),
    field({ decoder: NamedObject, version: 344, upgrade: (old) => NamedObject.create({ id: old, name: $t(`%Gr`) }) })
], DocumentTemplateGroup.prototype, "group", void 0);
__decorate([
    field({ decoder: NumberDecoder })
], DocumentTemplateGroup.prototype, "cycle", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(RecordAnswerDecoder) }),
    field({
        decoder: RecordAnswerMapDecoder,
        version: 252,
        upgrade: (old) => {
            const map = new Map();
            for (const answer of old) {
                map.set(answer.settings.id, answer);
            }
            return map;
        },
    })
], DocumentTemplateGroup.prototype, "fieldAnswers", void 0);
export class DocumentPrivateSettings extends AutoEncoder {
    templateDefinition = DocumentTemplateDefinition.create({});
    /**
     * Groups for which the members will receive the document
     */
    groups = [];
    customHtml = false;
}
__decorate([
    field({ decoder: DocumentTemplateDefinition })
], DocumentPrivateSettings.prototype, "templateDefinition", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(DocumentTemplateGroup) })
], DocumentPrivateSettings.prototype, "groups", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 343 })
], DocumentPrivateSettings.prototype, "customHtml", void 0);
export class DocumentTemplatePrivate extends AutoEncoder {
    id;
    status = DocumentStatus.Draft;
    isLocked = false;
    updatesEnabled = true;
    html = '';
    settings = DocumentSettings.create({});
    privateSettings = DocumentPrivateSettings.create({});
    year;
    createdAt = new Date();
    updatedAt = new Date();
    isRecordEnabled(_record) {
        return true;
    }
    getRecordAnswers() {
        return this.settings.fieldAnswers;
    }
    patchRecordAnswers(patch) {
        return this.patch({
            settings: DocumentSettings.patch({
                fieldAnswers: patch,
            }),
        });
    }
    doesMatchFilter(filter) {
        try {
            const recordAnswerFilters = {
                ...baseInMemoryFilterCompilers,
            };
            for (const a of this.settings.fieldAnswers.values()) {
                recordAnswerFilters[a.settings.id] = createInMemoryFilterCompiler([a.settings.id, 'objectValue']);
            }
            const defs = {
                ...documentInMemoryFilterCompilers,
                fieldAnswers: createInMemoryFilterCompiler('settings.fieldAnswers', recordAnswerFilters),
            };
            const compiledFilter = compileToInMemoryFilter(filter, defs);
            return compiledFilter(this);
        }
        catch (e) {
            console.error('Error while compiling filter', e, filter);
        }
        return false;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], DocumentTemplatePrivate.prototype, "id", void 0);
__decorate([
    field({ decoder: new EnumDecoder(DocumentStatus) })
], DocumentTemplatePrivate.prototype, "status", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 399 })
], DocumentTemplatePrivate.prototype, "isLocked", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], DocumentTemplatePrivate.prototype, "updatesEnabled", void 0);
__decorate([
    field({ decoder: StringDecoder })
], DocumentTemplatePrivate.prototype, "html", void 0);
__decorate([
    field({ decoder: DocumentSettings })
], DocumentTemplatePrivate.prototype, "settings", void 0);
__decorate([
    field({ decoder: DocumentPrivateSettings })
], DocumentTemplatePrivate.prototype, "privateSettings", void 0);
__decorate([
    field({ decoder: NumberDecoder, version: 391 })
], DocumentTemplatePrivate.prototype, "year", void 0);
__decorate([
    field({ decoder: DateDecoder })
], DocumentTemplatePrivate.prototype, "createdAt", void 0);
__decorate([
    field({ decoder: DateDecoder })
], DocumentTemplatePrivate.prototype, "updatedAt", void 0);
/**
 * Data stored with a document
 */
export class DocumentData extends AutoEncoder {
    name = '';
    description = '';
    /**
     * Contains a snapshot of all the answers
     */
    fieldAnswers = new Map();
}
__decorate([
    field({ decoder: StringDecoder })
], DocumentData.prototype, "name", void 0);
__decorate([
    field({ decoder: StringDecoder })
], DocumentData.prototype, "description", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(RecordAnswerDecoder) }),
    field({
        decoder: RecordAnswerMapDecoder,
        version: 252,
        upgrade: (old) => {
            const map = new Map();
            for (const answer of old) {
                map.set(answer.settings.id, answer);
            }
            return map;
        },
    })
], DocumentData.prototype, "fieldAnswers", void 0);
export class Document extends AutoEncoder {
    id;
    number = null;
    status = DocumentStatus.Draft;
    isLocked = false;
    data = DocumentData.create({});
    createdAt = new Date();
    updatedAt = new Date();
    memberId = null;
    registrationId = null;
    templateId;
    isRecordEnabled(_record) {
        return true;
    }
    getRecordAnswers() {
        return this.data.fieldAnswers;
    }
    patchRecordAnswers(patch) {
        return this.patch({
            data: DocumentData.patch({
                fieldAnswers: patch,
            }),
        });
    }
    doesMatchFilter(filter) {
        try {
            const compiledFilter = compileToInMemoryFilter(filter, documentInMemoryFilterCompilers);
            return compiledFilter(this);
        }
        catch (e) {
            console.error('Error while compiling filter', e, filter);
        }
        return false;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], Document.prototype, "id", void 0);
__decorate([
    field({ decoder: NumberDecoder, nullable: true, version: 179 })
], Document.prototype, "number", void 0);
__decorate([
    field({ decoder: new EnumDecoder(DocumentStatus) })
], Document.prototype, "status", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 399 })
], Document.prototype, "isLocked", void 0);
__decorate([
    field({ decoder: DocumentData })
], Document.prototype, "data", void 0);
__decorate([
    field({ decoder: DateDecoder })
], Document.prototype, "createdAt", void 0);
__decorate([
    field({ decoder: DateDecoder })
], Document.prototype, "updatedAt", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], Document.prototype, "memberId", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], Document.prototype, "registrationId", void 0);
__decorate([
    field({ decoder: StringDecoder })
], Document.prototype, "templateId", void 0);
//# sourceMappingURL=Document.js.map